<template>
  <div>
    <div
      class="form--group photo--section"
      :class="{ 'has-error': validation.hasError('listPhoto') }"
    >
      <div class="left--col">
        <div class="d-flex align-center">
          <span>{{ $t('addListing.photo') }}</span>
          <span class="required">*</span>
        </div>
        <ListingScoreField field-code="PHOTO_COUNT" />
      </div>

      <div class="right--col">
        <section-loading :show="loading" />

        <div class="photo--preview-wrapper" v-show="listPhoto && listPhoto.length > 0">
          <photo-preview
            v-for="(photo, index) in listPhoto"
            :key="photo.localId || `photo-${index}`"
            :index="index"
            :length="listPhoto.length"
            :photo="photo"
            :isFloorPlan="false"
            @retry="retryPhoto"
          />
        </div>

        <vue-dropzone
          v-show="false"
          ref="photoDropzone"
          id="dropzone"
          :options="dropzoneOptions"
          @vdropzone-file-added="added"
          @vdropzone-sending="sending"
          @vdropzone-success="success"
          @vdropzone-complete="completed"
          @vdropzone-error="onError"
        />

        <button
          :disabled="listPhoto.length >= 20"
          class="btn btn-primary--light btn-block"
          @click="triggerDropzone"
        >
          {{ $t('addListing.upload') }}
        </button>

        <span class="val-error" v-if="validation.hasError('listPhoto')">
          {{ validation.firstError('listPhoto') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import vue2Dropzone from 'nuxt-dropzone';
import { mapState } from 'vuex';
import { Validator } from 'simple-vue-validator';

const PhotoPreview = () => import('@/components/listing-form/sect2/photo/photo-preview.vue');

import SectionLoading from '@/components/content-loading/section-loading';
import ListingScoreField from '@/components/listing-form/listing-score-field.vue';
import { createDraftUuid } from '@/services/listingDraftRecovery';
import { getPhotos, putPhoto } from '@/services/listingDraftRepository';

export default {
  name: 'photo-section',

  components: {
    vueDropzone: vue2Dropzone,
    PhotoPreview,
    SectionLoading,
    ListingScoreField,
  },

  validators: {
    listPhoto(value) {
      return Validator.value(value).custom(() => {
        if (value.length <= 0) {
          return this.$i18n.t('errors.photo.required');
        }
      });
    },
  },

  data() {
    return {
      addedPhoto: true,
      loading: false,
      restoredLocalIds: new Set(),

      dropzoneOptions: {
        url: `${this.$store.state.global.apiUrl}/api/file_content/upload_photo_w`,

        thumbnailWidth: 150,
        thumbnailMethod: 'contain',

        maxFilesize: 5,

        acceptedFiles: '.jpg,.png,.jpeg,.heic,.heif,.jfif',

        createImageThumbnails: true,

        timeout: 60000,

        autoProcessQueue: false,
      },
    };
  },

  computed: {
    ...mapState({
      listPhoto: (state) => state.listingForm.sect2.media.photoList,

      apiUrl: (state) => state.global.apiUrl,

      form: (state) => state.listingForm.form,

      user: (state) => state.global.user,

      draftId: (state) => state.listingForm.localDraftId,

      localDraftHydrated: (state) => state.listingForm.localDraftHydrated,
    }),
  },

  methods: {
    validate() {
      return this.$validate().then((success) => {
        return !!success;
      });
    },

    triggerDropzone() {
      this.$refs.photoDropzone.dropzone.hiddenFileInput.click();
    },

    completed() {
      const dropzone = this.$refs.photoDropzone.dropzone;

      this.loading = dropzone.getUploadingFiles().length > 0;
    },

    /**
     * Check based on both MIME type and extension.
     *
     * Some browsers return an empty or inconsistent MIME type
     * for HEIC/HEIF files, so extension checking is important.
     */
    isHeicFile(file) {
      if (!file) {
        return false;
      }

      const fileName = (file.name || '').toLowerCase();
      const mimeType = (file.type || '').toLowerCase();

      return (
        mimeType === 'image/heic' ||
        mimeType === 'image/heif' ||
        mimeType === 'image/heic-sequence' ||
        mimeType === 'image/heif-sequence' ||
        fileName.endsWith('.heic') ||
        fileName.endsWith('.heif')
      );
    },

    /**
     * Create a browser-compatible preview URL.
     *
     * JPG/PNG/JFIF:
     *   Use the original Blob directly.
     *
     * HEIC/HEIF:
     *   Convert ONLY the preview to JPEG.
     *
     * The original File is never modified.
     */
    async createPreviewUrl(file) {
      if (!this.isHeicFile(file)) {
        return window.URL.createObjectURL(file);
      }

      try {
        /*
         * Lazy import is intentional.
         *
         * Users uploading normal JPG/PNG images don't need
         * to load the HEIC decoder.
         *
         * This is also preferable for Nuxt because the
         * conversion library is only needed in the browser.
         */
        const { heicTo } = await import('heic-to');

        const jpegBlob = await heicTo({
          blob: file,
          type: 'image/jpeg',
          quality: 0.8,
        });

        return window.URL.createObjectURL(jpegBlob);
      } catch (error) {
        console.error('Failed to create HEIC/HEIF preview:', error);

        return null;
      }
    },

    async added(file) {
      this.loading = true;

      const dropzone = this.$refs.photoDropzone;

      this.addedPhoto = true;

      if (!file.localDraftPhotoId && this.listPhoto.length >= 20) {
        dropzone.removeFile(file);

        this.loading = false;

        this.$swal(
          this.$t('errors.addListing.photo.maxTitle'),
          this.$i18n.t('errors.addListing.photo.maxMessage', {
            max: 20,
          }),
          'error',
        );

        return;
      }

      const localId = file.localDraftPhotoId || createDraftUuid();

      file.localDraftPhotoId = localId;

      let photo = this.listPhoto.find((item) => item.localId === localId);

      if (!photo) {
        /*
         * This is the important change.
         *
         * Previously:
         *
         * const objectUrl =
         *   window.URL.createObjectURL(file);
         *
         * That doesn't work for HEIC because <img>
         * usually can't decode HEIC.
         *
         * Now:
         *
         * HEIC -> temporary JPEG preview
         * JPG   -> original Blob URL
         */
        const previewUrl = await this.createPreviewUrl(file);

        /*
         * Keep storing the ORIGINAL file.
         *
         * Do NOT store the converted JPEG preview here.
         *
         * This means Laravel still receives the original
         * HEIC/HEIF and performs the authoritative
         * conversion using Imagick.
         */
        try {
          await putPhoto({
            draftId: this.draftId,
            localId,
            blob: file,
            fileName: file.name,
            fileType: file.type,
          });
        } catch (_) {
          await this.$swal({
            type: 'warning',

            title: 'Foto Tidak Tersimpan Lokal',

            text: 'Foto akan tetap diunggah, tetapi mungkin tidak pulih setelah browser ditutup.',

            confirmButtonText: 'OK',
          });
        }

        this.$store.commit('listingForm/sect2/media/ADD_PHOTO', {
          localId,

          fileName: file.name,

          /*
           * For HEIC this is a JPEG Blob URL.
           * For normal files this is the original Blob URL.
           */
          src: previewUrl,

          uuid: null,

          isPrimary: false,

          uploadStatus: 'pending',
        });

        photo = this.listPhoto.find((item) => item.localId === localId);
      }

      this.$store.commit('listingForm/sect2/media/UPDATE_PHOTO', {
        localId,

        changes: {
          uploadStatus: 'uploading',
        },
      });

      this.$nextTick(() => {
        dropzone.dropzone.processFile(file);
      });
    },

    sending(file, xhr) {
      this.loading = true;

      xhr.setRequestHeader('Authorization', `Bearer ${this.$store.state.global.token}`);

      this.$store.commit('listingForm/sect2/media/UPDATE_PHOTO', {
        localId: file.localDraftPhotoId,

        changes: {
          uploadStatus: 'uploading',
        },
      });

      const self = this;

      xhr.ontimeout = function () {
        console.log('DROPZONE TIMEOUT!');

        self.loading = false;

        self.$swal({
          type: 'error',

          title: self.$t('general.swal.photo.timeoutT'),

          html: self.$t('general.swal.photo.timeoutD'),

          reverseButtons: true,

          confirmButtonText: 'OK',
        });
      };
    },

    success(file, response) {
      /*
       * IMPORTANT:
       *
       * Do NOT replace src here.
       *
       * Your backend doesn't return a usable image URL,
       * so we keep the local JPEG preview Blob URL.
       *
       * For HEIC:
       *
       * photo.src
       *     ↓
       * blob:... containing JPEG
       *
       * while:
       *
       * photo.uuid
       *     ↓
       * points to the final JPEG stored by Laravel.
       */
      this.$store.commit('listingForm/sect2/media/UPDATE_PHOTO', {
        localId: file.localDraftPhotoId,

        changes: {
          uuid: response.data.photo_uuid,
          uploadStatus: 'uploaded',
        },
      });

      const dropzone = this.$refs.photoDropzone;

      dropzone.removeFile(file);
    },

    onError(file, message) {
      this.loading = false;

      if (file.localDraftPhotoId) {
        this.$store.commit('listingForm/sect2/media/UPDATE_PHOTO', {
          localId: file.localDraftPhotoId,

          changes: {
            uploadStatus: 'failed',
          },
        });
      }

      if (message && message.errors && message.errors.file && message.errors.file[0]) {
        this.$swal({
          type: 'error',

          title: 'Oops...',

          html: message.errors.file[0],

          reverseButtons: true,

          confirmButtonText: 'OK',
        });
      } else {
        this.$swal({
          type: 'error',

          title: 'Oops...',

          text: this.$t('general.swal.photo.errorD'),

          reverseButtons: true,

          confirmButtonText: 'OK',
        });
      }
    },

    async restorePendingPhotos() {
      if (!this.localDraftHydrated || !this.draftId || !this.$refs.photoDropzone) {
        return;
      }

      let storedPhotos = [];

      try {
        storedPhotos = await getPhotos(this.draftId);
      } catch (error) {
        console.error('Failed to restore local listing photos:', error);

        return;
      }

      for (const stored of storedPhotos) {
        const photo = this.listPhoto.find((item) => item.localId === stored.localId);

        if (!photo || photo.uuid || this.restoredLocalIds.has(stored.localId)) {
          continue;
        }

        this.restoredLocalIds.add(stored.localId);

        const file = new File([stored.blob], stored.fileName, {
          type: stored.fileType || stored.blob.type,
        });

        file.localDraftPhotoId = stored.localId;

        /*
         * Recreate the preview from IndexedDB.
         *
         * Blob URLs don't survive a full page/browser reload,
         * so rebuilding the URL here is useful.
         *
         * It also means a restored HEIC file gets converted
         * back to a JPEG preview again.
         */
        const previewUrl = await this.createPreviewUrl(file);

        if (previewUrl) {
          this.$store.commit('listingForm/sect2/media/UPDATE_PHOTO', {
            localId: stored.localId,

            changes: {
              src: previewUrl,
            },
          });
        }

        this.$refs.photoDropzone.dropzone.addFile(file);
      }
    },

    async retryPhoto(photo) {
      this.restoredLocalIds.delete(photo.localId);

      await this.restorePendingPhotos();
    },
  },

  watch: {
    localDraftHydrated: {
      immediate: true,

      handler() {
        this.$nextTick(() => this.restorePendingPhotos());
      },
    },
  },
};
</script>

<style scoped></style>
